/** @format — Shipment-scoped documents (received / expected / overdue) */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/services/python-api/queryKeys";
import { getAwaitingDocuments, listDocuments } from "@/services/python-api/documentsService";
import { mapApiDocumentToItem, formatUploadType } from "@/pages/documents/utils/documentFormatters";
import { unwrapDocumentsResponse } from "@/pages/documents/utils/unwrapDocumentsResponse";
import type { AwaitingDocumentItem, DocumentItem } from "@/pages/documents/types";

export type ShipmentDocumentTab = "received" | "expected" | "overdue";

export interface AwaitingDocumentRow extends AwaitingDocumentItem {
  label: string;
}

function mapAwaitingItems(payload: unknown): AwaitingDocumentRow[] {
  const body = (payload ?? {}) as { data?: { items?: AwaitingDocumentItem[] } };
  const items = body.data?.items ?? [];
  return items.map((item) => ({
    ...item,
    label: formatUploadType(item.uploadType),
  }));
}

function isNotFoundError(error: unknown) {
  return (
    typeof error === "object" &&
    error !== null &&
    "response" in error &&
    (error as { response?: { status?: number } }).response?.status === 404
  );
}

async function fetchAwaitingDocuments(
  listingId: string,
  awaitingType: "expected" | "overdue",
): Promise<AwaitingDocumentRow[]> {
  try {
    const payload = await getAwaitingDocuments(listingId, { awaitingType });
    return mapAwaitingItems(payload);
  } catch (error) {
    if (isNotFoundError(error)) return [];
    throw error;
  }
}

async function fetchReceivedDocuments(listingId: string): Promise<DocumentItem[]> {
  try {
    const payload = await listDocuments({ listingId, page: 0, limit: 100 });
    const unwrapped = unwrapDocumentsResponse(payload);
    const raw = Array.isArray(unwrapped.data) ? unwrapped.data : [];
    return raw.map(mapApiDocumentToItem);
  } catch (error) {
    if (isNotFoundError(error)) return [];
    throw error;
  }
}

const QUERY_OPTIONS = {
  staleTime: 30_000,
  retry: false,
  refetchOnWindowFocus: false,
} as const;

export function useShipmentDocuments(listingId?: string) {
  const receivedQuery = useQuery({
    queryKey: [QUERY_KEYS.DOCUMENTS, "listing", listingId, "received"],
    queryFn: () => fetchReceivedDocuments(listingId!),
    enabled: Boolean(listingId),
    ...QUERY_OPTIONS,
  });

  const expectedQuery = useQuery({
    queryKey: [QUERY_KEYS.AWAITING_DOCUMENTS, listingId, "expected"],
    queryFn: () => fetchAwaitingDocuments(listingId!, "expected"),
    enabled: Boolean(listingId),
    ...QUERY_OPTIONS,
  });

  const overdueQuery = useQuery({
    queryKey: [QUERY_KEYS.AWAITING_DOCUMENTS, listingId, "overdue"],
    queryFn: () => fetchAwaitingDocuments(listingId!, "overdue"),
    enabled: Boolean(listingId),
    ...QUERY_OPTIONS,
  });

  const counts = useMemo(
    () => ({
      received: receivedQuery.data?.length ?? 0,
      expected: expectedQuery.data?.filter((item) => !item.exists).length ?? 0,
      overdue: overdueQuery.data?.filter((item) => !item.exists).length ?? 0,
    }),
    [receivedQuery.data, expectedQuery.data, overdueQuery.data],
  );

  return {
    received: receivedQuery.data ?? [],
    expected: expectedQuery.data ?? [],
    overdue: overdueQuery.data ?? [],
    counts,
    isLoading:
      receivedQuery.isPending || expectedQuery.isPending || overdueQuery.isPending,
    isError: receivedQuery.isError || expectedQuery.isError || overdueQuery.isError,
  };
}
